/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  SnackbarCloseReason,
  Box,
  Typography,
  Divider,
  CircularProgress
} from '@mui/material';
import { BasicSelect, IVFormErrors, VForm, VTextField, useVForm } from '../../../shared/forms';
import * as yup from 'yup';
import { NfeServiceDistribuidas } from '../../../shared/services/api/Financeiro/fiscaldistribuidas';

interface IFormData {
  idresnfe: string;
  chave: string;
  xjust: string;
  tpevento: string;
}

export interface ConfirmationDialogRawProps {
  id: string;
  open: boolean;
  nome: string;
  data: string;
  valor?: number;
  dados?: IFormData;
  onClose: (value?: string) => void;
}

const formValidationSchema: yup.ObjectSchema<Omit<IFormData, 'xjust' | 'idresnfe' | 'chave'>> = yup.object().shape({
  tpevento: yup.string().required('Evento é obrigatório'),
});

export const DialoDistribuida: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {
  const { formRef, save } = useVForm();
  const { onClose, open, nome, data, valor, dados } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [showJust, setShowJust] = useState(false);

  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  useEffect(() => {
    if (open) {
      setMsg('');
      setOpenmsg(false);
      formRef.current?.setFieldValue('tpevento', '');
      formRef.current?.setFieldValue('xjust', '');
      setShowJust(false);
    }
  }, [open]);

  const handleSave = (dadosForm: IFormData) => {
    formValidationSchema
      .validate(dadosForm, { abortEarly: false })
      .then((dadosValidados) => {
        const payload: IFormData = {
          idresnfe: dados?.idresnfe ?? '',
          chave: dados?.chave ?? '',
          xjust: dadosForm.xjust ?? '',
          ...dadosValidados
        };

        setIsLoading(true);

        NfeServiceDistribuidas.createdistribuida(payload)
          .then((result) => {
            setIsLoading(false);

            if (result instanceof Error) {
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              setTms('success');
              setMsg('Operação realizada com sucesso!');
              setOpenmsg(true);

              setTimeout(() => {
                onClose('success');
              }, 1200);
            }
          })
          .catch((error) => {
            setIsLoading(false);
            setTms('error');
            setMsg('Erro ao processar a solicitação');
            setOpenmsg(true);
          });
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'grey.100',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={2} minWidth={0}>
              {/* Ícone opcional (pode trocar por outro) */}
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 24,
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}
              >
                NF
              </Box>

              <Box minWidth={0}>
                <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
                  Verificar Nota Emitida
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  mt={0.5}
                  noWrap
                  title={nome}
                >
                  {nome} • {data}
                </Typography>
              </Box>
            </Box>

            {valor !== undefined && (
              <Box textAlign="right" flexShrink={0}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Valor
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.dark" lineHeight={1.2}>
                  {valor.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogTitle>


        <Divider />

        <DialogContent sx={{ py: 3 }}>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={openmsg}
            autoHideDuration={3000}
            onClose={handleCloseMsg}
          >
            <Alert
              onClose={handleCloseMsg}
              severity={tms}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {msg}
            </Alert>
          </Snackbar>

          <VForm ref={formRef} onSubmit={handleSave}placeholder={undefined} onPointerEnterCapture={undefined}onPointerLeaveCapture={undefined}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <BasicSelect
                  name='tpevento'
                  label='Tipo de Evento'

                  data={[
                    { nameBD: '210200', descricao: 'Confirmação da Operação' },
                    { nameBD: '210210', descricao: 'Ciência da Operação' },
                    { nameBD: '210220', descricao: 'Desconhecimento da Operação' },
                    { nameBD: '210240', descricao: 'Operação não Realizada' },
                  ]}
                  disabled={isLoading}
                  aoClicar={(valor) => {
                    setShowJust(valor === '210220');
                    formRef.current?.setFieldValue('tpevento', valor);
                    formRef.current?.setFieldValue('xjust', ''); 
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <VTextField
                  fullWidth
                  name='xjust'
                  label='Justificativa'
                  disabled={!showJust || isLoading} // <-- só ativa quando showJust = true
                  multiline
                  rows={4}
                  size="medium"
                  helperText="Informe a justificativa"
                  placeholder="Descreva o motivo..."
                />

              </Grid>
            </Grid>
          </VForm>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
            disabled={isLoading}
            sx={{ minWidth: 120 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
            sx={{ minWidth: 120 }}
          >
            {isLoading ? 'Processando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};